import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import BackArrow from '../../assets/images/Frame_18177.png';
import EmailOneIMG from '../../assets/images/email_one.png';
import WhatsappIconIMG from '../../assets/images/whatsapp-icon.png';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import InactivityTimer from '../../components/InactivityTimer';

const RadiologyReports = () => {
    const [radiologyReports, setRadiologyReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewingReportUrl, setViewingReportUrl] = useState('');
    const [printedReports, setPrintedReports] = useState(new Set());
    const [printingReport, setPrintingReport] = useState(null);
    const [patientFullName, setPatientFullName] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const patientNameFromNav = location.state?.patientName || '';
    const token = useSelector((state) => state.auth.kiosk_token);

    // Validate token exists and redirect if not
    useEffect(() => {
        if (!token) {
            navigate('/login', { replace: true });
        }
    }, [token, navigate]);

    // Fetch and set reports on mount
    useEffect(() => {
        const fetchRadiologyReports = async () => {
            if (!token) return;
            try {
                setLoading(true);
                const decodedToken = jwtDecode(token);
                const response = await axios.get(
                    `${process.env.REACT_APP_KIOSK_OPS_URL}/radiology/reports/patientwise?mrno=${decodedToken.mrno}&visitId=0`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                        },
                    }
                );
                if (response.data && response.data.status === 'success' && Array.isArray(response.data.data)) {
                    setRadiologyReports(response.data.data);
                } else if (response.data && Array.isArray(response.data.data)) {
                    setRadiologyReports(response.data.data);
                }
                setLoading(false);
            } catch (err) {
                console.error("Error fetching radiology reports:", err);
                setError(err.message);
                setLoading(false);
            }
        };


        const fetchPatientData = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/patients/details`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response.data && response.data.patient) {
                    const patient = response.data.patient;

                    // Format full name: prefix + firstName + middleName + lastName
                    const nameParts = [
                        patient.prefix,
                        patient.firstName,
                        patient.middleName,
                        patient.lastName
                    ].filter(part => part && part.trim() !== '');

                    const fullName = nameParts.join(' ');
                    setPatientFullName(fullName);
                }
            } catch (err) {
                console.error("Error fetching patient data from MySQL:", err);
                // Fallback to external API if MySQL fetch fails
                try {
                    const decodedToken = jwtDecode(token);
                    const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(decodedToken.mrno)}`, {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^\"(.*)\"$/, '$1')}`,
                        },
                    });

                    if (response.data && response.data.data && response.data.data.length > 0) {
                        const patientData = response.data.data[0];

                        const nameParts = [
                            patientData.prefix,
                            patientData.firstName,
                            patientData.middleName,
                            patientData.lastName
                        ].filter(part => part && part.trim() !== '');

                        const fullName = nameParts.join(' ');
                        setPatientFullName(fullName);
                    }
                } catch (fallbackErr) {
                    console.error("Error fetching patient data from external API:", fallbackErr);
                }
            }
        };


        fetchRadiologyReports();
        fetchPatientData();
    }, [token]);

    // Decode token always after early return
    const decodedToken = token ? jwtDecode(token) : {};
    const userId = decodedToken?.id;

    // Check print statuses after reports are loaded
    useEffect(() => {
        const checkPrintStatuses = async () => {
            if (!userId || radiologyReports.length === 0) return;

            // Get unique orderId values and filter out undefined/null
            const orderIds = [...new Set(radiologyReports
                .map(r => r.orderId)
                .filter(id => id !== undefined && id !== null))];

            if (orderIds.length === 0) return; // Skip if no valid IDs

            const printedSet = new Set();
            for (const orderId of orderIds) {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/report-tracking/check-print-status`,
                        {
                            params: {
                                reportType: 'radiology',
                                reportId: orderId,
                                userId: userId
                            }
                        }
                    );
                    if (response.data.alreadyPrinted) {
                        printedSet.add(orderId);
                    }
                } catch (err) {
                    console.error('Error checking print status:', err);
                }
            }
            setPrintedReports(printedSet);
        };
        checkPrintStatuses();
    }, [radiologyReports, userId]);

    // Return early if no token to prevent rendering errors
    if (!token) {
        return null;
    }

    // Helper: format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            if (dateString.includes('-') && dateString.includes(':')) {
                const [datePart] = dateString.split(' ');
                return datePart;
            }
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    // Create report list for display
    const simplifiedReports = radiologyReports.map((report, index) => {
        let reportUrl = report.radiologyUrl || '';
        if (reportUrl) {
            reportUrl = reportUrl.replace(/\\u003d/g, '=').replace(/\\u0026/g, '&');
        }
        return {
            orderId: report.orderId || index + 1,
            testName: report.testName || 'N/A',
            testDate: report.orderConfirmedDate || report.createdDateTime || 'N/A',
            status: report.orderStatus || 'Completed',
            reportUrl: reportUrl,
            createdBy: report.createdBy || 'N/A',
            reportData: report
        };
    });

    // View tracking/modal
    const trackViewReport = async (orderId) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/report-tracking/view`, {
                reportType: 'radiology',
                reportId: orderId,
                userId: decodedToken?.id,
            });
        } catch (err) {
            console.error('Error tracking report view:', err);
        }
    };

    // Open modal to show PDF report and track view
    const handleViewReport = async (reportUrl, orderId) => {
        trackViewReport(orderId);

        // For PACS URLs, pass them directly as they may have embedded auth
        if (reportUrl && reportUrl.includes('PACS')) {
            setViewingReportUrl(reportUrl);
            setViewModalOpen(true);
            return;
        }

        // For regular PDF URLs, fetch with authorization headers
        try {
            const response = await axios.get(reportUrl, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                },
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            setViewingReportUrl(blobUrl);
            setViewModalOpen(true);
        } catch (err) {
            console.error('Error loading report:', err);
            // Fallback: try to open the original URL directly
            setViewingReportUrl(reportUrl);
            setViewModalOpen(true);
        }
    };

    // Print: direct, blob, only once, disable after print
    const printReportDirect = async (report) => {
        const id = report.orderId;
        if (printedReports.has(id)) {
            alert('You can only print this report once from the kiosk. If you need another copy, please contact reception.');
            return;
        }
        try {
            setPrintingReport(id);

            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/report-tracking/print`, {
                reportType: 'radiology',
                reportId: id,
                userId,
            });
            if (response.data.alreadyPrinted) {
                alert(response.data.message);
                setPrintedReports(prev => new Set([...prev, id]));
                setPrintingReport(null);
                return;
            }
            setPrintedReports(prev => new Set([...prev, id]));

            // Blob/fetch, print silent
            const pdfResponse = await fetch(report.reportUrl);
            const blob = await pdfResponse.blob();
            const blobUrl = URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = blobUrl;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                try {
                    iframe.contentWindow.print();
                    setTimeout(() => {
                        document.body.removeChild(iframe);
                        URL.revokeObjectURL(blobUrl);
                        setPrintingReport(null);
                    }, 1000);
                } catch (err) {
                    console.error('Error printing:', err);
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(blobUrl);
                    setPrintingReport(null);
                }
            };
        } catch (err) {
            console.error('Error tracking report print:', err);
            alert('Failed to print report. Please try again.');
            setPrintingReport(null);
        }
    };

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen overflow-x-hidden">
                <div className="flex flex-wrap items-center gap-3 px-4 sm:px-8 lg:px-14 pt-4 sm:pt-6">
                    <Link
                        to="/dashboard"
                        className="flex w-auto min-w-[7rem] px-4 py-2 sm:py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                        style={{ fontFamily: 'Roboto', fontSize: '20px', fontWeight: '500', lineHeight: '23.44px' }}
                    >
                        <img src={BackArrow} alt="Back" className="mr-2 w-5 h-5 sm:w-6 sm:h-6" />
                        Back
                    </Link>
                    <span className="text-xl sm:text-2xl lg:text-[28px] font-semibold" style={{ fontFamily: 'Roboto', fontWeight: '600', lineHeight: '1.2' }}>
                        Radiology Reports
                    </span>
                </div>
                <hr className="mt-4 sm:mt-6 mx-4 sm:mx-8 lg:mx-14 border-t border-gray-300" />
            {loading ? (
                <div className="flex items-center justify-center min-h-[50vh] sm:min-h-[60vh] px-4">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-[#0FA968] mx-auto mb-4"></div>
                        <p className="text-base sm:text-xl text-gray-600">Loading Radiology Reports...</p>
                    </div>
                </div>
            ) : error ? (
                <div className="flex items-center justify-center min-h-[50vh] sm:min-h-[60vh] px-4">
                    <div className="text-center">
                        <h1 className="text-2xl sm:text-4xl font-bold text-red-500 mb-4">Error</h1>
                        <p className="text-base sm:text-xl text-gray-600">{error}</p>
                    </div>
                </div>
            ) : (
                <>
                    {radiologyReports.length > 0 && (
                        <div className="mx-4 sm:mx-8 lg:mx-14 mt-4 sm:mt-6 bg-gradient-to-r from-[#0FA968] to-[#138F5E] text-white p-4 sm:p-6 rounded-xl shadow-lg max-w-[1600px]">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                <div>
                                    <p className="text-xs sm:text-sm opacity-90">Patient Name</p>
                                    <p className="text-base sm:text-lg font-semibold break-words">{patientFullName || decodedToken.name || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-xs sm:text-sm opacity-90">MRN</p>
                                    <p className="text-base sm:text-lg font-semibold break-words">{decodedToken.mrno}</p>
                                </div>
                                <div>
                                    <p className="text-xs sm:text-sm opacity-90">Total Reports</p>
                                    <p className="text-base sm:text-lg font-semibold">{simplifiedReports.length}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="mx-4 sm:mx-8 lg:mx-14 my-4 sm:my-6 max-w-[1600px]">
                        {simplifiedReports.length > 0 ? (
                            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[720px]">
                                        <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                                            <tr>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold uppercase">Test Name</th>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold uppercase">Test Date</th>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold uppercase">Status</th>
                                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm font-semibold uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {simplifiedReports.map((report, index) => (
                                                <tr key={report.orderId || index} className={`hover:bg-blue-50 transition ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <p className="font-semibold text-gray-800 text-sm sm:text-base break-words">{report.testName}</p>
                                                        <p className="text-xs text-gray-500">Order ID: {report.orderId}</p>
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <span className="text-gray-700 font-medium text-sm sm:text-base whitespace-nowrap">{report.testDate}</span>
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 whitespace-nowrap">
                                                            {report.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <div className="flex flex-wrap justify-center items-center gap-2">
                                                            <button
                                                                onClick={() => handleViewReport(report.reportUrl, report.orderId)}
                                                                className="bg-[#0FA968] text-white rounded-lg px-3 sm:px-4 py-2 font-semibold hover:bg-[#138F5E] transition duration-200 shadow text-xs sm:text-sm whitespace-nowrap">
                                                                View Full Report
                                                            </button>
                                                            <button
                                                                onClick={() => printReportDirect(report)}
                                                                disabled={printedReports.has(report.orderId) || printingReport !== null}
                                                                className={`bg-transparent border border-[#0FA968] rounded-lg p-2 transition duration-200 ${printedReports.has(report.orderId) || printingReport !== null
                                                                    ? 'opacity-50 cursor-not-allowed bg-gray-200'
                                                                    : 'hover:bg-[#0FA968] hover:text-white'
                                                                    }`}
                                                                title={printedReports.has(report.orderId) ? 'Already printed - Contact reception for more copies' : 'Print report'}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="green">
                                                                    <path d="M6 2h12v4H6V2zm12 6H6c-1.1 0-2 .9-2 2v6h4v4h8v-4h4v-6c0-1.1-.9-2-2-2zm-4 10H10v-2h4v2z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                className='bg-[#EFFEF7] rounded-lg px-3 py-2 border-2 border-[#0FA968] hover:bg-[#0FA968] hover:bg-opacity-10 transition duration-200'>
                                                                <img src={EmailOneIMG} alt='Email' className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                className='bg-[#EFFEF7] rounded-lg px-3 py-2 border-2 border-[#0FA968] hover:bg-[#0FA968] hover:bg-opacity-10 transition duration-200'>
                                                                <img src={WhatsappIconIMG} alt='Whatsapp' className="w-5 h-5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center min-h-[30vh] sm:min-h-[40vh] px-4">
                                <div className="text-center">
                                    <svg className="w-16 h-16 sm:w-24 sm:h-24 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2">No Radiology Reports Found</h3>
                                    <p className="text-sm sm:text-base text-gray-500">There are no radiology reports available for this patient.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
            {
                viewModalOpen && (
                    <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-75 report-modal-container px-2 sm:px-4">
                        <div className="relative bg-white rounded-lg shadow-lg w-full max-w-6xl h-[85vh] sm:h-[90vh]">
                            <button
                                onClick={() => {
                                    setViewModalOpen(false);
                                }}
                                className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
                                aria-label="Close"
                            >
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <iframe
                                src={viewingReportUrl}
                                title="Radiology Report"
                                className="w-full h-full rounded-lg report-modal-iframe"
                                frameBorder="0"
                                referrerPolicy="no-referrer"
                            />
                        </div>
                    </div>
                )
            }

            {
                printingReport !== null && (
                    <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-75 px-4">
                        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 text-center w-full max-w-sm">
                            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-[#0FA968] border-t-transparent mx-auto mb-4"></div>
                            <p className="text-lg sm:text-xl font-semibold text-gray-800">Preparing to print...</p>
                            <p className="text-xs sm:text-sm text-gray-600 mt-2">Please wait while we process your report</p>
                        </div>
                    </div>
                )
            }
            </div>
        </>
    );
};

export default RadiologyReports;